import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { research, runGroup, type ResearchResult, type Run, type Source } from "../lib/research";
import { useBusiness } from "../lib/business";

const PATTERN_LABEL: Record<string, string> = {
  "f.s1": "firstname.surname", "f1.s1": "firstname.surname", "f1.sL": "firstname.lastword", "f.sA": "firstname.all-surnames",
  "f1s1": "firstnamesurname", "fis1": "fsurname", "fi.s1": "f.surname", "f1_s1": "firstname_surname", "f1-s1": "firstname-surname",
  "s1.f1": "surname.firstname", "s1fi": "surnamef", "s1f1": "surnamefirstname", "f1": "firstname", "s1": "surname",
  "fj.s1": "firstnames.surname", "f.sP": "firstname.sur-name",
};
const CONF_ORDER = { high: 0, medium: 1, low: 2 } as const;
// Groups researched at the same time. Each is an independent search; four keeps a
// full role list to a few minutes without piling requests onto the API.
const PARALLEL = 4;

function parseJson<T>(s: string | null, fallback: T): T {
  if (!s) return fallback;
  try { return JSON.parse(s) as T; } catch { return fallback; }
}

export default function ResearchRun() {
  const { id } = useParams<{ id: string }>();
  const runId = Number(id);
  const [params, setParams] = useSearchParams();
  const [run, setRun] = useState<Run | null>(null);
  const [results, setResults] = useState<ResearchResult[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [running, setRunning] = useState<Set<number>>(new Set());
  const busyRunning = running.size > 0;
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [assignTo, setAssignTo] = useState<number | "">("");
  const [minConf, setMinConf] = useState<"low" | "medium" | "high">("low");
  const stopRef = useRef(false);
  const { businesses } = useBusiness();
  const startedRef = useRef(false);

  const load = useCallback(async () => {
    try {
      const d = await research.run(runId);
      setRun(d.run);
      setResults(d.results);
      return d.run;
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      return null;
    }
  }, [runId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!busyRunning) return;
    const t0 = Date.now();
    setElapsed(0);
    const t = setInterval(() => setElapsed(Math.round((Date.now() - t0) / 1000)), 1000);
    return () => clearInterval(t);
  }, [busyRunning]);

  const runGroups = useCallback(async (indexes: number[], force = false) => {
    stopRef.current = false;
    setError(null);
    const queue = [...indexes];
    const worker = async () => {
      for (let i = queue.shift(); i !== undefined; i = queue.shift()) {
        if (stopRef.current) return;
        const idx = i;
        setRunning(prev => new Set(prev).add(idx));
        const ev = await runGroup(runId, idx, force);
        if (ev.type === "error") setError(`Group ${idx + 1}: ${ev.error}`);
        setRunning(prev => { const n = new Set(prev); n.delete(idx); return n; });
        await load();
      }
    };
    await Promise.all(Array.from({ length: Math.min(PARALLEL, queue.length) }, worker));
  }, [runId, load]);

  // Coming straight from "Run research": work through the pending groups.
  useEffect(() => {
    if (!run || startedRef.current || params.get("autostart") !== "1") return;
    startedRef.current = true;
    setParams({}, { replace: true });
    const pending = run.groups.map((g, i) => (g.status === "pending" ? i : -1)).filter(i => i >= 0);
    if (pending.length) runGroups(pending);
  }, [run, params, setParams, runGroups]);

  // Warn before leaving while a group is mid-flight — closing the tab aborts it.
  useEffect(() => {
    if (!busyRunning) return;
    const h = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [busyRunning]);

  const visible = useMemo(() => {
    const floor = CONF_ORDER[minConf];
    return results
      .filter(r => CONF_ORDER[r.confidence || "low"] <= floor)
      .sort((a, b) => CONF_ORDER[a.confidence || "low"] - CONF_ORDER[b.confidence || "low"]);
  }, [results, minConf]);

  if (!run) return <div className="card p-6 text-sm text-slate-500">{error || "Loading…"}</div>;

  const pendingIdx = run.groups.map((g, i) => (g.status === "pending" || g.status === "error" ? i : -1)).filter(i => i >= 0);
  const picked = results.filter(r => selected.has(r.id));
  const allVisibleSelected = visible.length > 0 && visible.every(r => selected.has(r.id));

  function toggle(id: number) {
    const n = new Set(selected);
    if (n.has(id)) n.delete(id); else n.add(id);
    setSelected(n);
  }

  // Master only finds people; assigning makes them contacts of one company, where
  // they are verified and emailed.
  async function assignSelected() {
    const company = businesses.find(b => b.id === assignTo);
    if (!company) { setError("Choose the company to assign to."); return; }
    const noEmail = picked.filter(r => !r.email).length;
    if (!confirm(
      `Assign ${picked.length} selected people to ${company.name}?\n\n` +
      `They become ${company.name} contacts — verify and send from ${company.name} → Contacts.` +
      (noEmail ? `\n\n${noEmail} of them have no email yet.` : "")
    )) return;
    setBusy(true); setError(null);
    try {
      const res = await research.assign(runId, picked.map(r => r.id), company.id);
      const created = res.contacts.filter(c => c.created).length;
      await load();
      setSelected(new Set());
      alert(`Assigned to ${company.name}: ${created} new contact(s)${res.contacts.length - created ? `, ${res.contacts.length - created} already there` : ""}.`);
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  }

  async function saveEmail(r: ResearchResult, email: string) {
    if ((r.email || "") === email.trim().toLowerCase()) return;
    try {
      await research.setEmail(r.id, email);
      setResults(prev => prev.map(x => x.id === r.id ? { ...x, email: email.trim().toLowerCase() || null, email_basis: email.trim() ? "manual" : "none" } : x));
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  }

  const ev = run.evidence;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tightish text-slate-900">
            {run.company}{run.country && <span className="text-slate-400"> · {run.country}</span>}
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Research #{run.id} · {results.length} people · spent USD ${Number(run.cost_usd || 0).toFixed(2)} ({run.web_searches} web searches)
            {run.created_by_email && <> · by {run.created_by_email}</>}
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/outreach/research" className="btn-secondary">← Research</Link>
          <button className="btn-danger" disabled={busyRunning} onClick={async () => {
            if (!confirm("Delete this research run and its results? People already assigned stay in their company's contacts.")) return;
            await research.deleteRun(runId);
            window.location.href = "/outreach/research";
          }}>Delete</button>
        </div>
      </div>

      {error && <div className="card p-4 text-sm text-rose-700">{error}</div>}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="card p-4 lg:col-span-2">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm font-semibold">Position groups</span>
            <div className="flex gap-2">
              {busyRunning
                ? <button className="btn-secondary !text-xs !py-1 !px-2" onClick={() => { stopRef.current = true; }}>Don't start more groups</button>
                : pendingIdx.length > 0 && (
                  <button className="btn-primary !text-xs !py-1 !px-2" onClick={() => runGroups(pendingIdx)}>
                    Run {pendingIdx.length} remaining
                  </button>
                )}
            </div>
          </div>
          {busyRunning && (
            <div className="mb-2 rounded bg-indigo-50 px-3 py-2 text-xs text-indigo-800">
              Researching {running.size} group{running.size === 1 ? "" : "s"} at once ({[...running].map(i => run.groups[i]?.name).join(", ")}) — {elapsed}s. Keep this page open.
            </div>
          )}
          <ul className="divide-y divide-slate-100">
            {run.groups.map((g, i) => (
              <li key={i} className="flex items-start gap-3 py-2 text-xs">
                <span className={`pill mt-0.5 ${
                  running.has(i) ? "pill-blue" : g.status === "done" ? "pill-green" : g.status === "error" ? "pill-rose" : "pill-slate"
                }`}>{running.has(i) ? "running" : g.status}</span>
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-slate-800">{g.name} <span className="font-normal text-slate-400">· {g.roles.length} positions</span></div>
                  <div className="truncate text-slate-500" title={g.roles.join(", ")}>{g.roles.join(", ")}</div>
                  {g.error && <div className="text-rose-700">{g.error}</div>}
                </div>
                <div className="whitespace-nowrap text-right text-slate-500">
                  {g.status === "done" && <>{g.found} found{g.cost_usd != null && <> · ${g.cost_usd.toFixed(2)}</>}</>}
                  {!busyRunning && (g.status === "done" || g.status === "error") && (
                    <button className="ml-2 text-indigo-600 hover:underline" onClick={() => {
                      if (g.status === "done" && !confirm(`Re-run “${g.name}”? It costs money again; people already found are kept.`)) return;
                      runGroups([i], g.status === "done");
                    }}>{g.status === "done" ? "Re-run" : "Retry"}</button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="card space-y-2 p-4 text-xs text-slate-600">
          <div className="text-sm font-semibold text-slate-900">Email format</div>
          {run.email_domain
            ? ev?.pattern
              ? <>
                  <div><strong className="font-mono text-slate-900">{PATTERN_LABEL[ev.pattern] || ev.pattern}@{run.email_domain}</strong></div>
                  <div>Learned from {ev.matches} of {ev.domain_total} addresses on {run.email_domain} already in Contacts{ev.same_country ? ` (${run.country})` : ""}.</div>
                  {ev.sample?.length > 0 && <ul className="list-disc pl-4 text-slate-500">{ev.sample.map(s => <li key={s}>{s}</li>)}</ul>}
                </>
              : <div>Domain <strong>{run.email_domain}</strong>, but no known addresses to learn the format from — guesses use firstname.surname.</div>
            : <div>No domain yet. It is learned from Contacts, the domain you typed, or one found published during research.</div>}
          <div className="border-t border-slate-100 pt-2 text-slate-500">
            <strong>Guessed</strong> addresses are unverified until NeverBounce says <span className="pill pill-green">valid</span>. A valid result proves the mailbox exists — on a shared group domain it may belong to someone else with the same name.
          </div>
        </div>
      </div>

      <div className="card overflow-hidden">
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 p-3 text-xs">
          <span className="font-semibold text-slate-900">People found</span>
          <label className="ml-2 flex items-center gap-1">Show
            <select className="!py-0.5 !text-xs" value={minConf} onChange={e => setMinConf(e.target.value as typeof minConf)}>
              <option value="low">all confidence levels</option>
              <option value="medium">medium + high</option>
              <option value="high">high only</option>
            </select>
          </label>
          <div className="ml-auto flex items-center gap-1.5">
            <span className="text-slate-500">{selected.size} selected</span>
            <select className="!py-1 !text-xs" value={assignTo} onChange={e => setAssignTo(e.target.value ? Number(e.target.value) : "")}>
              <option value="">Assign to company…</option>
              {businesses.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
            <button className="btn-primary !text-xs !py-1 !px-2" disabled={busy || !selected.size || !assignTo} onClick={assignSelected}>Assign</button>
          </div>
        </div>
        {visible.length === 0
          ? <div className="p-6 text-center text-sm text-slate-500">{busyRunning ? "Results appear here as each group finishes." : "No people found yet."}</div>
          : (
            <div className="overflow-x-auto">
              <table className="refined [&_tbody_td]:!px-3 [&_tbody_td]:!py-2 [&_tbody_td]:!text-xs [&_thead_th]:!px-3 [&_thead_th]:!py-2">
                <thead>
                  <tr>
                    <th className="cell-check"><input type="checkbox" checked={allVisibleSelected} onChange={() => {
                      const n = new Set(selected);
                      if (allVisibleSelected) visible.forEach(r => n.delete(r.id)); else visible.forEach(r => n.add(r.id));
                      setSelected(n);
                    }} /></th>
                    <th>Person</th>
                    <th>Covers</th>
                    <th>Confidence</th>
                    <th>Sources</th>
                    <th className="min-w-[260px]">Email</th>
                    <th>Assigned to</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map(r => {
                    const sources = parseJson<Source[]>(r.sources_json, []);
                    const roles = parseJson<string[]>(r.matched_roles, []);
                    const alts = parseJson<string[]>(r.alt_emails, []);
                    return (
                      <tr key={r.id}>
                        <td className="cell-check"><input type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)} /></td>
                        <td className="max-w-[260px]">
                          <div className="font-medium text-slate-900">{r.full_name}</div>
                          <div className="text-slate-600">{r.current_title}</div>
                          {r.notes && <div className="mt-0.5 text-[11px] text-amber-700">{r.notes}</div>}
                        </td>
                        <td className="max-w-[200px] text-slate-600">{roles.join("; ")}</td>
                        <td>
                          <span className={`pill ${r.confidence === "high" ? "pill-green" : r.confidence === "medium" ? "pill-amber" : "pill-slate"}`}>{r.confidence}</span>
                          {r.evidence_date && <div className="mt-0.5 text-[11px] text-slate-400">{r.evidence_date}</div>}
                        </td>
                        <td className="max-w-[240px]">
                          <ul className="space-y-0.5">
                            {sources.map(s => (
                              <li key={s.url} className="truncate">
                                {!s.seen && <span className="mr-1 text-amber-600" title="This link was not among the search results Claude saw — check it before trusting it">⚠</span>}
                                <a href={s.url} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline" title={s.url}>{s.title || s.url}</a>
                              </li>
                            ))}
                          </ul>
                        </td>
                        <td>
                          {<input
                                key={`${r.id}:${r.email}`}
                                className="w-full font-mono !text-xs !py-1 !px-2"
                                defaultValue={r.email || ""}
                                placeholder="no email"
                                onBlur={e => saveEmail(r, e.target.value)}
                              />}
                          <div className="mt-0.5 text-[11px]">
                            {r.email_basis === "public" && <span className="text-emerald-700">published{r.public_email_source && <> · <a className="underline" href={r.public_email_source} target="_blank" rel="noreferrer">source</a></>}</span>}
                            {(r.email_basis === "pattern" || r.email_basis === "domain-only") && <span className="text-amber-700">guessed ({r.email_basis === "pattern" ? "learned format" : "default format"}) — unverified</span>}
                            {r.email_basis === "manual" && <span className="text-slate-500">edited by hand</span>}
                          </div>
                          {alts.length > 0 && (
                            <div className="mt-0.5 flex flex-wrap gap-1 text-[10.5px] text-slate-400">
                              or {alts.map(a => (
                                <button key={a} className="font-mono hover:text-indigo-600 hover:underline" onClick={() => saveEmail(r, a)}>{a.split("@")[0]}</button>
                              ))}
                            </div>
                          )}
                        </td>
                        <td>
                          {r.assignments.length === 0
                            ? <span className="text-slate-400">—</span>
                            : <div className="flex flex-col gap-1">
                                {r.assignments.map(a => (
                                  <span key={a.business_id} className={`pill ${a.verification_status === "valid" ? "pill-green" : a.verification_status === "invalid" ? "pill-rose" : "pill-slate"}`}
                                        title={`${a.email || "no email"} · ${a.verification_status}`}>
                                    {businesses.find(b => b.id === a.business_id)?.name || `#${a.business_id}`}
                                    {a.verification_status !== "unchecked" && <> · {a.verification_status}</>}
                                  </span>
                                ))}
                              </div>}
                        </td>
                        <td>
                          {r.assignments.length === 0 && (
                            <button className="text-slate-400 hover:text-rose-600" title="Remove this person from the results"
                                    onClick={async () => { await research.deleteResult(r.id); setResults(prev => prev.filter(x => x.id !== r.id)); }}>×</button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
      </div>

    </div>
  );
}
